import {ReactElement, useCallback, useEffect, useRef} from 'react';

import {useDispatch, useSelector} from 'react-redux';
import scroller from 'react-scroll/modules/mixins/scroller';

import {useApplePayAvailability} from 'hooks/useApplePayStripe/useApplePayAvailability';
import {useSelectorRef} from 'hooks/useSelectorRef/useSelectorRef';
import {flags} from 'libs/rollout';

import {STEPS} from '../../constants/checkout';
import {PAYMENTS, PAYMENTS_WITH_AUTO_SELECTION} from '../../constants/paymentStep';
import {setSelectedMethod} from '../../store/reducers/paymentStep/selectedMethod/selectedMethod';
import {
    getPaymentMethods,
    getPaymentMethodsLoading,
    getSelectedMethod,
} from '../../store/selectors/paymentStep/paymentStep';

/**
 * Set default payment method on payment step based on content of the element
 */
export const useSetDefaultPayment = (
    filteredPayments: {
        paymentCode: string;
        content: ReactElement | null;
    }[],
) => {
    const isApplePayAvailable = useApplePayAvailability();
    const dispatch = useDispatch();
    const filteredPaymentsRef = useRef(filteredPayments);
    filteredPaymentsRef.current = filteredPayments;

    const selectedMethodRef = useSelectorRef(getSelectedMethod);
    const paymentMethodsRef = useSelectorRef(getPaymentMethods);
    const isPaymentsLoading = useSelector(getPaymentMethodsLoading);

    const shouldIgnorePaymentMethod = useCallback(
        (paymentMethodCode: string) =>
            (paymentMethodCode === PAYMENTS.METHOD_APPLE_PAY && !isApplePayAvailable) ||
            (paymentMethodCode === PAYMENTS.METHOD_CREDIT_CARD &&
                flags.checkoutPrimerCreditCard.isEnabled()),
        [isApplePayAvailable],
    );

    const getDefaultMethod = useCallback(() => {
        const {list} = paymentMethodsRef.current;
        const listValues = Object.values(list);
        const method = listValues.find(item => {
            if (shouldIgnorePaymentMethod(item.code)) return false;
            return item.selected;
        });

        return method?.code as PAYMENTS;
    }, [paymentMethodsRef, shouldIgnorePaymentMethod]);

    useEffect(() => {
        if (isPaymentsLoading) return;
        const defaultPaymentMethod = filteredPaymentsRef.current.find(method => {
            if (shouldIgnorePaymentMethod(method.paymentCode)) return false;

            return method.content;
        });

        const selectedMethod = selectedMethodRef.current;
        const findMethod = getDefaultMethod() || (defaultPaymentMethod?.paymentCode as PAYMENTS);

        if (
            !selectedMethod ||
            !PAYMENTS_WITH_AUTO_SELECTION.includes(selectedMethod.code as PAYMENTS)
        ) {
            dispatch(setSelectedMethod(findMethod));
        }

        scroller.scrollTo(STEPS.PAYMENT_STEP, {
            duration: 1000,
            isDynamic: true,
            offset: -100,
            smooth: true,
        });
    }, [
        isApplePayAvailable,
        isPaymentsLoading,
        selectedMethodRef,
        getDefaultMethod,
        dispatch,
        shouldIgnorePaymentMethod,
    ]);
};
